import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Package, CheckCircle, Send, Ruler, Search, AlertTriangle, Minus, Plus, X, SkipForward } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { validateBundleFormat } from '@/utils/validation';
import StaffIdInput from '@/components/StaffIdInput';

const GreigeInspection = () => {
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffIdLocked, setStaffIdLocked] = useState(false);
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [bundleLocked, setBundleLocked] = useState(false);
  const [bundleData, setBundleData] = useState(null);
  const [staffStats, setStaffStats] = useState({ today_count: 0, month_count: 0 });

  const [productType, setProductType] = useState('dhoti');
  const [runningMtrQty, setRunningMtrQty] = useState('');
  const [runningMtrLocked, setRunningMtrLocked] = useState(false);

  const [reed, setReed] = useState('');
  const [pick, setPick] = useState('');
  const [width, setWidth] = useState('');
  const [length, setLength] = useState('');
  const [kuriGap, setKuriGap] = useState('');
  const [measurementsLocked, setMeasurementsLocked] = useState(false);

  const [damageTypes, setDamageTypes] = useState([]);
  const [damageSearchQuery, setDamageSearchQuery] = useState('');
  // Map: damage_code -> { damage_code, damage_name, count }
  const [damageCounts, setDamageCounts] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);
  const runningMtrInputRef = useRef(null);

  useEffect(() => {
    staffInputRef.current?.focus();
    fetchDamageTypes();
  }, []);

  useEffect(() => {
    if (staffIdLocked && staffId) loadStaffStats();
  }, [staffIdLocked, staffId]);

  const loadStaffStats = async () => {
    try {
      const response = await apiClient.get(`/sections/greige/staff-stats/${staffId}`, {
        params: { operation_name: 'Greige Inspection' }
      });
      setStaffStats({
        today_count: response.data.today_count || 0,
        month_count: response.data.month_count || 0
      });
    } catch (e) { /* non-blocking */ }
  };

  const fetchDamageTypes = async () => {
    try {
      const response = await apiClient.get('/sections/greige/damage-types');
      setDamageTypes(response.data.damages || []);
    } catch (e) {
      console.error('Error fetching damage types:', e);
    }
  };

  const handleStaffValidated = (result) => {
    if (result.valid) {
      setStaffId(result.staffId);
      setStaffName(result.staffName || '');
      setStaffIdLocked(true);
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    }
  };

  const handleBundleValidation = async (e) => {
    e.preventDefault();
    const code = bundleBarcode.trim();
    if (!code) { toast.error('Please scan bundle barcode'); return; }
    const formatValidation = validateBundleFormat(code);
    if (!formatValidation.valid) {
      toast.error(formatValidation.error);
      setBundleBarcode('');
      bundleInputRef.current?.focus();
      return;
    }
    try {
      const response = await apiClient.post('/sections/greige/inspection/validate-bundle', null, {
        params: { bundle_barcode: code }
      });
      setBundleData(response.data);
      setBundleLocked(true);
      toast.success('Bundle validated');
      if (productType === 'running_mtr') setTimeout(() => runningMtrInputRef.current?.focus(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid bundle');
      setBundleBarcode('');
      bundleInputRef.current?.focus();
    }
  };

  const handleRunningMtrSubmit = (e) => {
    e.preventDefault();
    if (!runningMtrQty || parseFloat(runningMtrQty) <= 0) {
      toast.error('Please enter a valid quantity in meters');
      return;
    }
    setRunningMtrLocked(true);
    toast.success(`Quantity set: ${runningMtrQty} meters`);
  };

  const handleMeasurementsSubmit = (e) => {
    e.preventDefault();
    if (!reed || !pick || !width || !length || !kuriGap) {
      toast.error('All measurements are required');
      return;
    }
    setMeasurementsLocked(true);
  };

  const addDamage = (damage) => {
    setDamageCounts(prev => {
      const cur = prev[damage.damage_code];
      return {
        ...prev,
        [damage.damage_code]: {
          damage_code: damage.damage_code,
          damage_name: damage.damage_name,
          count: (cur?.count || 0) + 1,
        }
      };
    });
  };

  const decDamage = (damageCode) => {
    setDamageCounts(prev => {
      const cur = prev[damageCode];
      if (!cur) return prev;
      const next = { ...prev };
      if (cur.count <= 1) delete next[damageCode];
      else next[damageCode] = { ...cur, count: cur.count - 1 };
      return next;
    });
  };

  const removeDamage = (damageCode) => {
    setDamageCounts(prev => {
      const next = { ...prev };
      delete next[damageCode];
      return next;
    });
  };

  const recorded = Object.values(damageCounts);
  const totalOccurrences = recorded.reduce((s, d) => s + d.count, 0);

  const filteredDamageTypes = damageTypes.filter(d =>
    d.damage_name?.toLowerCase().includes(damageSearchQuery.toLowerCase()) ||
    d.damage_code?.toLowerCase().includes(damageSearchQuery.toLowerCase())
  );

  const resetForm = () => {
    setBundleBarcode('');
    setBundleLocked(false);
    setBundleData(null);
    setRunningMtrQty('');
    setRunningMtrLocked(false);
    setReed(''); setPick(''); setWidth(''); setLength(''); setKuriGap('');
    setMeasurementsLocked(false);
    setDamageCounts({});
    setDamageSearchQuery('');
  };

  const handleNotInspected = async () => {
    if (isSubmitting) return;
    if (!window.confirm('Mark this bundle as "Not Inspected"? Basic details and damages will be skipped and the bundle will be completed (cannot be inspected again).')) {
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await apiClient.post('/sections/greige/inspection/submit', {
        staff_id: staffId,
        bundle_barcode: bundleBarcode,
        product_type: productType,
        running_mtr_qty: null,
        reed: null,
        pick: null,
        width: null,
        length: null,
        kuri_gap: null,
        bundle_damages: [],
        not_inspected: true,
      });
      toast.success(response.data.message || 'Bundle marked as Not Inspected');
      resetForm();
      loadStaffStats();
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (productType === 'running_mtr' && (!runningMtrQty || parseFloat(runningMtrQty) <= 0)) {
      toast.error('Running meter quantity is required');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await apiClient.post('/sections/greige/inspection/submit', {
        staff_id: staffId,
        bundle_barcode: bundleBarcode,
        product_type: productType,
        running_mtr_qty: productType === 'running_mtr' ? parseFloat(runningMtrQty) : null,
        reed: parseInt(reed),
        pick: parseInt(pick),
        width: parseFloat(width),
        length: parseFloat(length),
        kuri_gap: parseFloat(kuriGap),
        bundle_damages: recorded.map(d => ({ damage_code: d.damage_code, damage_name: d.damage_name, count: d.count })),
      });
      toast.success(response.data.message || 'Inspection recorded');
      resetForm();
      loadStaffStats();
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to submit inspection');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/sections/greige')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">Operation 4: Greige Inspection</h1>
              <p className="text-sm text-[#E8DCC8]">Record bundle damages (no USN scanning — qty assigned later at Reconcile)</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Step 1: Staff */}
        <Card className="border-2 border-[#E5DFD0]">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-orange-600">1</div>
              <User className="w-5 h-5 text-orange-600" />
              Staff Identification
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!staffIdLocked ? (
              <StaffIdInput inputRef={staffInputRef} onValidated={handleStaffValidated} autoFocus={true} label="" />
            ) : (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-orange-600 text-white px-4 py-2 rounded-lg font-mono text-lg" data-testid="locked-staff-id">{staffId}</div>
                  {staffName && <span className="text-[#5A5F6E]">{staffName}</span>}
                  <div className="text-sm text-[#5A5F6E]">
                    <span className="font-semibold text-orange-600">{staffStats.today_count}</span> today {' · '}
                    <span className="font-semibold text-orange-600">{staffStats.month_count}</span> this month
                  </div>
                </div>
                <Button variant="outline" onClick={() => { setStaffIdLocked(false); setStaffId(''); setStaffName(''); resetForm(); }}>Change</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Bundle Validation */}
        {staffIdLocked && (
          <Card className={`border-2 ${bundleLocked ? 'border-green-500 bg-green-50/30' : 'border-orange-600'}`}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-orange-600">2</div>
                <Package className="w-5 h-5 text-orange-600" />
                Bundle Validation
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!bundleLocked ? (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-1.5 block">Product Type</Label>
                    <Select value={productType} onValueChange={setProductType}>
                      <SelectTrigger className="w-full md:w-64" data-testid="product-type-select">
                        <SelectValue placeholder="Select product type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dhoti">Dhoti</SelectItem>
                        <SelectItem value="running_mtr">Running Mtr</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <form onSubmit={handleBundleValidation} className="flex gap-3">
                    <Input ref={bundleInputRef} value={bundleBarcode} onChange={(e) => setBundleBarcode(e.target.value)} placeholder="Scan Bundle QR code..." className="text-lg h-12 font-mono" autoComplete="off" data-testid="bundle-input" />
                    <Button type="submit" className="h-12 bg-orange-600 hover:bg-orange-700" data-testid="validate-bundle-btn">
                      <CheckCircle className="w-5 h-5 mr-2" /> Validate
                    </Button>
                  </form>
                </div>
              ) : (
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded">
                  <div>
                    <span className="text-xs px-2 py-0.5 rounded bg-orange-200 text-orange-800 font-medium">
                      {productType === 'running_mtr' ? 'Running Mtr' : 'Dhoti'}
                    </span>
                    <p className="font-mono font-bold text-orange-900 mt-1" data-testid="locked-bundle">{bundleBarcode}</p>
                    <p className="text-sm text-orange-700">Vendor: {bundleData?.vendor_name} • Sort: {bundleData?.sort_no}</p>
                    {bundleData?.color_name && (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-200 text-orange-900" data-testid="inspection-color">
                        Color: {bundleData.color_name}
                      </span>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={resetForm}>Change</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Not Inspected — skip basic details + damages and complete (behaves like No Damage) */}
        {bundleLocked && (
          <Card className="border-2 border-amber-400 bg-amber-50/40" data-testid="not-inspected-card">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="bg-amber-100 p-2 rounded-lg">
                  <SkipForward className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900">Not Inspected</p>
                  <p className="text-sm text-amber-700 max-w-xl">
                    Skip basic details &amp; damage recording and complete this bundle. It is recorded as <strong>&quot;Not Inspected&quot;</strong> and moves on to Reconcile (same as No Damage).
                  </p>
                </div>
              </div>
              <Button
                onClick={handleNotInspected}
                disabled={isSubmitting}
                variant="outline"
                className="border-amber-500 text-amber-800 hover:bg-amber-100 whitespace-nowrap"
                data-testid="not-inspected-btn"
              >
                {isSubmitting
                  ? <div className="w-4 h-4 border-2 border-amber-700 border-t-transparent rounded-full animate-spin mr-2" />
                  : <Send className="w-4 h-4 mr-2" />}
                Mark Not Inspected &amp; Submit
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2.5: Running Meter Quantity */}
        {bundleLocked && productType === 'running_mtr' && (
          <Card className={`border-2 ${runningMtrLocked ? 'border-green-500 bg-green-50/30' : 'border-orange-600'}`}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Ruler className="w-5 h-5 text-orange-600" /> Running Meter Quantity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!runningMtrLocked ? (
                <form onSubmit={handleRunningMtrSubmit} className="flex gap-3">
                  <Input ref={runningMtrInputRef} type="number" step="0.01" min="0.01" value={runningMtrQty} onChange={(e) => setRunningMtrQty(e.target.value)} placeholder="Enter quantity in meters..." className="text-lg h-12 font-mono" autoComplete="off" data-testid="running-mtr-input" />
                  <Button type="submit" className="h-12 bg-orange-600 hover:bg-orange-700"><CheckCircle className="w-5 h-5 mr-2" /> Confirm</Button>
                </form>
              ) : (
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded">
                  <p className="text-2xl font-bold text-orange-900">{runningMtrQty} <span className="text-lg font-normal">meters</span></p>
                  <Button variant="outline" size="sm" onClick={() => { setRunningMtrLocked(false); setMeasurementsLocked(false); }}>Change</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Measurements (basic details) */}
        {bundleLocked && (productType === 'dhoti' || runningMtrLocked) && (
          <Card className={`border-2 ${measurementsLocked ? 'border-green-500 bg-green-50/30' : 'border-orange-600'}`}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-orange-600">3</div>
                Basic Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!measurementsLocked ? (
                <form onSubmit={handleMeasurementsSubmit} className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div><Label>Reed *</Label><Input type="number" value={reed} onChange={(e) => setReed(e.target.value)} placeholder="0" className="mt-1" data-testid="reed-input" required /></div>
                    <div><Label>Pick *</Label><Input type="number" value={pick} onChange={(e) => setPick(e.target.value)} placeholder="0" className="mt-1" data-testid="pick-input" required /></div>
                    <div><Label>Width (m) *</Label><Input type="number" step="0.01" value={width} onChange={(e) => setWidth(e.target.value)} placeholder="0.00" className="mt-1" data-testid="width-input" required /></div>
                    <div><Label>Length (m) *</Label><Input type="number" step="0.01" value={length} onChange={(e) => setLength(e.target.value)} placeholder="0.00" className="mt-1" data-testid="length-input" required /></div>
                    <div><Label>Kuri Gap *</Label><Input type="number" step="0.01" value={kuriGap} onChange={(e) => setKuriGap(e.target.value)} placeholder="0.00" className="mt-1" data-testid="kuri-gap-input" required /></div>
                  </div>
                  <Button type="submit" className="w-full h-12 bg-orange-600 hover:bg-orange-700" data-testid="confirm-measurements-btn">
                    <CheckCircle className="w-5 h-5 mr-2" /> Confirm & Record Damages
                  </Button>
                </form>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div className="p-2 bg-orange-50 rounded"><span className="text-orange-700 font-medium">Reed:</span> {reed}</div>
                    <div className="p-2 bg-orange-50 rounded"><span className="text-orange-700 font-medium">Pick:</span> {pick}</div>
                    <div className="p-2 bg-orange-50 rounded"><span className="text-orange-700 font-medium">Width:</span> {width}m</div>
                    <div className="p-2 bg-orange-50 rounded"><span className="text-orange-700 font-medium">Length:</span> {length}m</div>
                    <div className="p-2 bg-orange-50 rounded"><span className="text-orange-700 font-medium">Kuri Gap:</span> {kuriGap}</div>
                  </div>
                  <Button variant="outline" size="sm" className="w-full" onClick={() => setMeasurementsLocked(false)}>Edit</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 4: Record Damages */}
        {measurementsLocked && (
          <Card className="border-2 border-orange-600">
            <CardHeader className="pb-3 bg-orange-50">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold bg-orange-600">4</div>
                  <AlertTriangle className="w-5 h-5 text-orange-600" /> Record Damages
                </CardTitle>
                <Button onClick={handleSubmit} disabled={isSubmitting} className="bg-orange-600 hover:bg-orange-700" data-testid="submit-inspection-btn">
                  {isSubmitting ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Submit Inspection
                </Button>
              </div>
              <CardDescription className="ml-10">Tap a damage to add an occurrence. Bundles with no damage can be submitted directly.</CardDescription>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              {/* Recorded damages summary */}
              {recorded.length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="recorded-damages">
                  <p className="text-sm font-semibold text-red-800 mb-2">Recorded: {recorded.length} type(s) • {totalOccurrences} occurrence(s)</p>
                  <div className="flex flex-wrap gap-2">
                    {recorded.map(d => (
                      <div key={d.damage_code} className="flex items-center gap-1 bg-white border border-red-300 rounded-full pl-3 pr-1 py-1 text-sm" data-testid={`recorded-${d.damage_code}`}>
                        <span className="max-w-[160px] truncate">{d.damage_name}</span>
                        <button onClick={() => decDamage(d.damage_code)} data-testid={`recorded-dec-${d.damage_code}`} className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-red-100 text-red-600"><Minus className="w-3 h-3" /></button>
                        <span className="font-bold text-red-700 w-5 text-center" data-testid={`count-${d.damage_code}`}>{d.count}</span>
                        <button onClick={() => addDamage({ damage_code: d.damage_code, damage_name: d.damage_name })} data-testid={`recorded-inc-${d.damage_code}`} className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-green-100 text-green-600"><Plus className="w-3 h-3" /></button>
                        <button onClick={() => removeDamage(d.damage_code)} data-testid={`recorded-remove-${d.damage_code}`} className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-500"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Search damage types..." value={damageSearchQuery} onChange={(e) => setDamageSearchQuery(e.target.value)} className="pl-10" data-testid="damage-search" />
              </div>

              {/* Damage buttons grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[420px] overflow-y-auto pr-1">
                {filteredDamageTypes.length === 0 ? (
                  <p className="col-span-full text-center text-gray-500 py-6">No damage types found</p>
                ) : filteredDamageTypes.map(d => {
                  const cnt = damageCounts[d.damage_code]?.count || 0;
                  return (
                    <button
                      key={d.damage_code}
                      onClick={() => addDamage(d)}
                      data-testid={`damage-btn-${d.damage_code}`}
                      className={`relative text-left px-3 py-2.5 rounded-lg border-2 transition-colors ${cnt > 0 ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-white hover:border-orange-400 hover:bg-orange-50'}`}
                    >
                      <span className="text-sm text-[#1A1F2E] block pr-7">{d.damage_name}</span>
                      {cnt > 0 && (
                        <span className="absolute top-1.5 right-1.5 min-w-[22px] h-[22px] px-1 flex items-center justify-center rounded-full bg-red-600 text-white text-xs font-bold">{cnt}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default GreigeInspection;
